import { ChangeDetectionStrategy, Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { ContractLedger } from '@wsphere/warranties/domain';
import { PartyListItem, RatingMatrixService } from '../../services';
import { escapeHtml } from '../../utils/escape-html.utils';

// No Seller: sellers are the existing distributors, resolved from the quick sale or the cell's Seller dimension.
const ROLE_DESCRIPTIONS: Partial<Record<ContractLedger.PartyType, string>> = {
  [ContractLedger.PartyType.INSURER]: 'Underwriter backing the contract’s risk.',
  [ContractLedger.PartyType.AGENT]: 'Agent or producer owed part of the price.',
  [ContractLedger.PartyType.ADMINISTRATOR]: 'Administers the contract — usually the tenant itself.',
  [ContractLedger.PartyType.PLATFORM]: 'A platform-level fee recipient.',
  [ContractLedger.PartyType.OTHER]: 'Any other counterparty.',
};

export const PARTY_ROLE_OPTIONS: { role: ContractLedger.PartyType; label: string; description: string }[] =
  ContractLedger.PARTY_RECORD_ROLES.map(role => ({
    role,
    label: ContractLedger.partyTypeLabel(role),
    description: ROLE_DESCRIPTIONS[role] ?? '',
  }));

/** Creates or edits a fee-recipient party: its name and the roles it may receive fees in. */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-party-dialog',
  templateUrl: './party.dialog.html',
  standalone: false,
})
export class PartyDialog {
  @ViewChild(DialogComponent) dialog!: DialogComponent;

  @Output() closed = new EventEmitter<boolean>();

  readonly roleOptions = PARTY_ROLE_OPTIONS;

  party: PartyListItem | null = null;
  name = '';
  roles = new Set<ContractLedger.PartyType>();
  saving = false;

  constructor(
    private ratingMatrixSvc: RatingMatrixService,
    private toastSvc: ToastService
  ) {}

  get canSave(): boolean {
    return !this.saving && !!this.name.trim() && this.roles.size > 0;
  }

  open(party?: PartyListItem) {
    this.party = party ?? null;
    this.name = party?.name ?? '';
    this.roles = new Set(party?.roles ?? []);
    this.saving = false;
    this.dialog.open();
  }

  isRoleInUse(role: ContractLedger.PartyType): boolean {
    // Dropping a role still assigned somewhere is rejected by the API; flag it up front.
    return !!this.party && this.party.roles.includes(role) && this.party.usage.length > 0;
  }

  toggleRole(role: ContractLedger.PartyType, checked: boolean) {
    if (checked) this.roles.add(role);
    else this.roles.delete(role);
  }

  cancel() {
    this.dialog.close();
    this.closed.emit(false);
  }

  async save() {
    if (!this.canSave) return;

    this.saving = true;
    const name = this.name.trim();
    try {
      await this.ratingMatrixSvc.saveParty(
        {
          name,
          // Preserve the canonical role order rather than click order.
          roles: this.roleOptions.map(option => option.role).filter(role => this.roles.has(role)),
        },
        this.party?.id
      );
      this.toastSvc.show({
        type: 'success',
        title: this.party ? 'Party updated' : 'Party created',
        text: `<strong>${escapeHtml(name)}</strong> has been saved.`,
      });
      this.dialog.close();
      this.closed.emit(true);
    } catch (err) {
      this.toastSvc.show({
        type: 'error',
        title: 'Unable to save party',
        text: escapeHtml((err as { error?: { message?: string } })?.error?.message ?? 'Something went wrong.'),
      });
    } finally {
      this.saving = false;
    }
  }
}
